import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

/**
 * Bottom-centre toasts, at most two stacked, each gone after 2.6 s. Success
 * feedback is a single clause ("Karta unieważniona"); errors stay inline next
 * to the form. A toast slides in and fades out, so a second one arriving
 * right after the first reads as a sequence rather than a flicker.
 */
const ToastContext = createContext<((message: string) => void) | null>(null);

interface Toast {
  id: number;
  message: string;
  leaving: boolean;
}

const SHOW_MS = 2600;
const EXIT_MS = 160;
const MAX = 2;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<number, number[]>());
  const seq = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    timers.current.get(id)?.forEach((t) => window.clearTimeout(t));
    timers.current.delete(id);
  }, []);

  const show = useCallback(
    (text: string) => {
      const id = ++seq.current;
      setToasts((list) => {
        // The oldest leaves at once when a third arrives; two is the cap.
        const kept = list.slice(-(MAX - 1));
        list.slice(0, -(MAX - 1)).forEach((t) => remove(t.id));
        return [...kept, { id, message: text, leaving: false }];
      });
      const leave = window.setTimeout(() => setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t))), SHOW_MS);
      const gone = window.setTimeout(() => remove(id), SHOW_MS + EXIT_MS);
      timers.current.set(id, [leave, gone]);
    },
    [remove],
  );

  useEffect(() => {
    const all = timers.current;
    return () => all.forEach((ts) => ts.forEach((t) => window.clearTimeout(t)));
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toasts.length > 0 && (
        <div className="pointer-events-none fixed inset-x-4 bottom-6 z-[60] flex flex-col items-center gap-2 max-[899px]:bottom-[calc(96px+env(safe-area-inset-bottom))]">
          {toasts.map((t) => (
            <div
              key={t.id}
              role="status"
              className={`pointer-events-auto max-w-full truncate rounded-md border border-line-strong bg-panel-3 px-4 py-2.5 text-[13px] font-medium shadow-float ${
                t.leaving ? "rp-exit-toast" : "rp-enter-toast"
              }`}
            >
              {t.message}
            </div>
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast(): (message: string) => void {
  const show = useContext(ToastContext);
  if (!show) throw new Error("useToast must be used inside ToastProvider");
  return show;
}
